import { LegalFormEnum } from '~/src/models/enums/legal-form.enum';
import { RegNumbersEnum } from '~/src/models/enums/reg-numbers.enum';
import type { RegNumbersServiceInterface } from '~/src/models/types/reg-numbers-service.types';
import type { regNumbersType } from '~/src/components/common-components/reg-number-input/reg-number-input.types';

/**
 * Used in Module Federation
 */
const RegNumbersService: RegNumbersServiceInterface = {
    getConstants() {
        return {
            MAX_INN_LEGAL_LENGTH: 10,
            MAX_INN_INDIVIDUAL_LENGTH: 12,
            MAX_INN_SELF_EMPLOYER_LENGTH: 12,
            MAX_OGRN_INDIVIDUAL_LENGTH: 15,
            MAX_OGRN_LEGAL_LENGTH: 13,
            MAX_KPP_LENGTH: 9,
        };
    },

    checkValidLength(regNumbersType: regNumbersType, value: string, legalForm: LegalFormEnum) {
        switch (regNumbersType) {
            case RegNumbersEnum.inn:
                return this.isValidINN(value, legalForm);
            case RegNumbersEnum.ogrn:
                return this.isValidOGRN(value, legalForm);
            case RegNumbersEnum.kpp:
                return this.isValidKPP(value);
            default:
                return this.isValidINN(value, legalForm);
        }
    },

    getMaxLength(legalForm: LegalFormEnum, regNumbersType: regNumbersType): number {
        switch (regNumbersType) {
            case RegNumbersEnum.inn:
                return legalForm === LegalFormEnum.INDIVIDUAL_ENTREPRENEUR || legalForm === LegalFormEnum.SELF_EMPLOYEE
                    ? this.getConstants().MAX_INN_INDIVIDUAL_LENGTH
                    : this.getConstants().MAX_INN_LEGAL_LENGTH;
            case RegNumbersEnum.ogrn:
                return legalForm === LegalFormEnum.INDIVIDUAL_ENTREPRENEUR
                    ? this.getConstants().MAX_OGRN_INDIVIDUAL_LENGTH
                    : this.getConstants().MAX_OGRN_LEGAL_LENGTH;
            case RegNumbersEnum.kpp:
                return this.getConstants().MAX_KPP_LENGTH;
            default:
                return this.getConstants().MAX_OGRN_LEGAL_LENGTH;
        }
    },

    checkSumINN() {
        const MAX_INN_LEGAL_LENGTH = this.getConstants().MAX_INN_LEGAL_LENGTH;
        const MAX_INN_INDIVIDUAL_LENGTH = this.getConstants().MAX_INN_INDIVIDUAL_LENGTH;
        const MAX_INN_SELF_EMPLOYEE_LENGTH = this.getConstants().MAX_INN_SELF_EMPLOYEE_LENGTH;
        return {
            legalEntity(inn: number[]): boolean {
                return (
                    inn.length === MAX_INN_LEGAL_LENGTH &&
                    inn[9] ===
                        ((2 * inn[0] +
                            4 * inn[1] +
                            10 * inn[2] +
                            3 * inn[3] +
                            5 * inn[4] +
                            9 * inn[5] +
                            4 * inn[6] +
                            6 * inn[7] +
                            8 * inn[8]) %
                            11) %
                            10
                );
            },
            entrepreneur(inn: number[]): boolean {
                return (
                    (inn.length === MAX_INN_INDIVIDUAL_LENGTH || inn.length === MAX_INN_SELF_EMPLOYEE_LENGTH) &&
                    inn[10] ===
                        ((7 * inn[0] +
                            2 * inn[1] +
                            4 * inn[2] +
                            10 * inn[3] +
                            3 * inn[4] +
                            5 * inn[5] +
                            9 * inn[6] +
                            4 * inn[7] +
                            6 * inn[8] +
                            8 * inn[9]) %
                            11) %
                            10 &&
                    inn[11] ===
                        ((3 * inn[0] +
                            7 * inn[1] +
                            2 * inn[2] +
                            4 * inn[3] +
                            10 * inn[4] +
                            3 * inn[5] +
                            5 * inn[6] +
                            9 * inn[7] +
                            4 * inn[8] +
                            6 * inn[9] +
                            8 * inn[10]) %
                            11) %
                            10
                );
            },
        };
    },

    checkOGRN() {
        const MAX_OGRN_INDIVIDUAL_LENGTH = this.getConstants().MAX_OGRN_INDIVIDUAL_LENGTH;
        const MAX_OGRN_LEGAL_LENGTH = this.getConstants().MAX_OGRN_LEGAL_LENGTH;
        return {
            entrepreneur(ogrn: number[]): boolean {
                return (
                    ogrn.length === MAX_OGRN_INDIVIDUAL_LENGTH &&
                    ogrn[14] === Math.floor((Number(ogrn.join('')) / 10) % 13) % 10
                );
            },
            legalEntity(ogrn: number[]): boolean {
                return (
                    ogrn.length === MAX_OGRN_LEGAL_LENGTH &&
                    ogrn[12] === Math.floor((Number(ogrn.join('')) / 10) % 11) % 10
                );
            },
        };
    },

    isValidKPP(value: string | number): boolean {
        const valueToString = value ? value.toString() : '';
        return !(
            valueToString.length !== this.getConstants().MAX_KPP_LENGTH || !/\d{4}[\dA-Z]{2}\d{3}/.test(valueToString)
        );
    },

    isValidINN(value: string | number, legalForm: LegalFormEnum): boolean {
        const inn = [...String(value)].map(Number);
        switch (legalForm) {
            case LegalFormEnum.LEGAL_ENTITY:
                return this.checkSumINN().legalEntity(inn);
            case LegalFormEnum.INDIVIDUAL_ENTREPRENEUR:
                return this.checkSumINN().entrepreneur(inn);
            case LegalFormEnum.SELF_EMPLOYEE:
                return this.checkSumINN().entrepreneur(inn);
            default:
                return false;
        }
    },

    isValidOGRN(value: string | number, legalForm: LegalFormEnum): boolean {
        const ogrn = [...String(value)].map(Number);
        switch (legalForm) {
            case LegalFormEnum.LEGAL_ENTITY:
                return this.checkOGRN().legalEntity(ogrn);
            case LegalFormEnum.INDIVIDUAL_ENTREPRENEUR:
                return this.checkOGRN().entrepreneur(ogrn);
            default:
                return false;
        }
    },
};

export default RegNumbersService;
